/*
 * GEHC CONFIDENTIAL
 *
 * Copyright (c) 2026, 2026, GE HealthCare
 * All Rights Reserved
 *
 * This unpublished material is proprietary to GE HealthCare. The methods and
 * techniques described herein are considered trade secrets and/or
 * confidential. Reproduction or distribution, in whole or in part, is
 * forbidden except by express written permission of GE HealthCare.
 * GE is a trademark of General Electric Company used under trademark license.
 */

import { Injectable } from '@angular/core';
import { AuditService } from '@app/common/audit.service';

/**
 * Audit trail service for Digital Assistant (DA) FHIRcast events.
 *
 * Each method corresponds to a discrete FHIRcast lifecycle event:
 *   - WFM → DA outbound publishes (imagingstudy-select, home-open, userLogout)
 *   - DA → WFM inbound events (syncsuccess, syncerror, DA-initiated home-open)
 *
 * Delegates to AuditService.saveAuditAction, which resolves participant/context
 * information from CurrentExamContextService when examAuditInfo is not supplied.
 */
@Injectable({
  providedIn: 'root'
})
export class DaAuditService {
  constructor(private auditService: AuditService) {}

  // ── Outbound: WFM → DA ──────────────────────────────────────────────────────

  /** imagingstudy-select published and syncsuccess received from DA */
  logStudySelectSuccess(examAuditInfo?) {
    this.auditService.saveAuditAction(
      this.auditService.getActionCode().Read,
      this.auditService.getActionType().DaStudySelectSuccess,
      this.auditService.getEventOutcome().Success,
      examAuditInfo
    );
  }

  /** imagingstudy-select published but sync failed or timed out */
  logStudySelectFailure(examAuditInfo?) {
    this.auditService.saveAuditAction(
      this.auditService.getActionCode().Read,
      this.auditService.getActionType().DaStudySelectFailure,
      this.auditService.getEventOutcome().MinorFailure,
      examAuditInfo
    );
  }

  /** home-open published and syncsuccess received from DA */
  logHomeOpenSuccess(examAuditInfo?) {
    this.auditService.saveAuditAction(
      this.auditService.getActionCode().Execute,
      this.auditService.getActionType().DaHomeOpen,
      this.auditService.getEventOutcome().Success,
      examAuditInfo
    );
  }

  /** home-open published but sync failed or timed out */
  logHomeOpenFailure(examAuditInfo?) {
    this.auditService.saveAuditAction(
      this.auditService.getActionCode().Execute,
      this.auditService.getActionType().DaHomeOpenFailure,
      this.auditService.getEventOutcome().MinorFailure,
      examAuditInfo
    );
  }

  /** userLogout published to DA */
  logUserLogout(examAuditInfo?) {
    this.auditService.saveAuditAction(
      this.auditService.getActionCode().Execute,
      this.auditService.getActionType().DaUserLogout,
      this.auditService.getEventOutcome().Success,
      examAuditInfo
    );
  }

  // ── Inbound: DA → WFM ───────────────────────────────────────────────────────

  /** DA-initiated home-open received (user navigated home inside DA) */
  logHomeOpenReceived(examAuditInfo?) {
    this.auditService.saveAuditAction(
      this.auditService.getActionCode().Execute,
      this.auditService.getActionType().DaHomeOpenReceived,
      this.auditService.getEventOutcome().Success,
      examAuditInfo
    );
  }

  /** syncsuccess received from DA for any outbound event */
  logSyncSuccess(examAuditInfo?) {
    this.auditService.saveAuditAction(
      this.auditService.getActionCode().Execute,
      this.auditService.getActionType().DaSyncSuccess,
      this.auditService.getEventOutcome().Success,
      examAuditInfo
    );
  }

  /** syncerror received from DA for any outbound event */
  logSyncError(examAuditInfo?) {
    this.auditService.saveAuditAction(
      this.auditService.getActionCode().Execute,
      this.auditService.getActionType().DaSyncError,
      this.auditService.getEventOutcome().MinorFailure,
      examAuditInfo
    );
  }
}
